import * as React from 'react';

import { DataTable } from '@/components/dataTable';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { exportCsvSingleFilePapa, filterCsvDataByVisibleColumns } from '@/lib/exportCsv';
import { formatAmount } from '@/lib/format';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import type { customerPaymentLog } from '@/services/customerPaymentLogs/customerPaymentLogs.types';
import { getCustomerPaymentStatusLabel } from '../utils/getCustomerPaymentStatusLabel';
import { getColumns } from './Column';

export function CustomerPaymentTable({
  data = [],
  pagination,
  loading,
}: {
  data?: customerPaymentLog[];
  pagination: Pagination;
  loading: boolean;
}) {
  const { t } = useDynamicLocale();
  const { onOpen } = useDialogFormStore();
  const EXPORT_KEYS_BY_COLUMN: Record<string, readonly string[]> = {
    sn: ['SN'],
    requestID: [t('common.page.tableColumn.requestId')],
    userInfo: [t('common.page.tableColumn.userInfo')],
    emailAddress: [t('common.page.tableColumn.emailAddress')],
    phoneNumber: [t('common.page.tableColumn.phoneNumber')],
    requestDate: [t('common.page.tableColumn.requestDate')],
    requestedAmount: [t('common.page.tableColumn.requestedAmount')],
    approvalDate: [t('common.page.tableColumn.approvalDate')],
    remarks: [t('common.page.tableColumn.remarks')],
    status: [t('common.page.tableColumn.status')],
  };
  const columns = React.useMemo(() => getColumns(onOpen, t), [onOpen, t]);
  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );
  const handleExport = () => {
    const exportData = table.getFilteredRowModel().rows.map(({ original: row, index }) => ({
      SN: getSerialNumber(table, index),
      [t('common.page.tableColumn.requestId')]: row.withdrawalRequestId,
      [t('common.page.tableColumn.userInfo')]: row.userInfo,
      [t('common.page.tableColumn.emailAddress')]: row.emailAddress,
      [t('common.page.tableColumn.phoneNumber')]: row.phoneNumber,
      [t('common.page.tableColumn.requestDate')]: row.requestDate,
      [t('common.page.tableColumn.requestedAmount')]: formatAmount(row.requestedAmount),
      [t('common.page.tableColumn.approvalDate')]: row.approvalDate,
      [t('common.page.tableColumn.remarks')]: row.remarks,
      [t('common.page.tableColumn.status')]: getCustomerPaymentStatusLabel(row.status, t),
    }));
    const visibleColumnIds = table.getVisibleLeafColumns().map((column) => column.id);
    const visibleData = filterCsvDataByVisibleColumns(exportData, visibleColumnIds, EXPORT_KEYS_BY_COLUMN);

    exportCsvSingleFilePapa(visibleData, {
      fileName: 'customer-payment-logs-report',
      format: 'YYYY/MM/DD HH:mm:ss',
    });
  };

  const { table } = useDataTable({
    data,
    columns,
    pageCount,
    initialState: {
      sorting: [{ id: 'requestDate', desc: true }],
    },
    meta: {
      title: <Translation id="paymentManagement.customerPaymentLogs.table.title" />,
      totalPage: pagination.totalItems,
      loading,
      actionKey: 'exportCsv',
      action: handleExport,
    },
    getRowId: (row) => row.paymentLogSno,
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <DataTable table={table} className="font-medium">
      <DataTableToolbar />
    </DataTable>
  );
}
